//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025,2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { io, Socket } from 'socket.io-client';
import settings from '../../../Config/settings';

export type {
  ToolApprovalRequest,
  IterationWarningEvent,
  HitlDecision,
  HitlAction,
  ContentType,
  SummarizeChunkData,
  SummarizeCompleteData,
  ThinkingChunkData,
} from '../../../types/streaming';

import type {
  HitlDecision,
  IterationWarningEvent,
  PlanApprovalRequest,
  ToolApprovalRequest,
} from '../../../types/streaming';
import type {
  ExecutionReceiptEvent,
  ExecutionTerminalEvent,
} from '../../../types/executionReceipt';

export type {
  ExecutionReceiptEvent,
  ExecutionTerminalEvent,
} from '../../../types/executionReceipt';

export interface StreamingEvent {
  event:
    | 'workflow_started'
    | 'node_started'
    | 'node_completed'
    | 'agent_selected'
    | 'tool_call'
    | 'validation'
    | 'final_response'
    | 'analysis_complete'
    | 'execution_complete'
    | 'plan_created'
    | 'SUMMARIZE_START'
    | 'SUMMARIZE_CHUNK'
    | 'SUMMARIZE_COMPLETE'
    | 'THINKING_CHUNK'
    | 'STEP_START'
    | 'STEP_COMPLETED'
    | 'TOOL_START'
    | 'TOOL_COMPLETE'
    | 'tool_call_start'
    | 'tool_call_result'
    | 'error';
  data: unknown;
  timestamp: string;
}

export interface ConnectionStatus {
  isConnected: boolean;
  reason?: string;
}

export interface ApprovalRequest {
  action: string;
  details: any;
  reason: string;
  timestamp: string;
}

export interface ClarificationQuestion {
  question: string;
  parameter?: string;
  length?: number;
}

export interface ClarificationRequest {
  questions?: ClarificationQuestion[];
  clarification_text: string;
  options?: string[];
  context: string;
  timestamp: string;
}

export interface ChatCompleteEvent {
  success: boolean;
  message: string;
  conversation_id: string;
  error?: string;
}

// The messages with which the server refuses a handshake whose token is missing or
// cannot be verified: analyticsai-server's connect handler ("authentication required" /
// "authentication failed") and its older connect-time check ("Invalid or expired token").
const AUTH_REFUSALS = [
  'authentication required',
  'authentication failed',
  'Invalid or expired token',
];
// Socket.IO retries a transport failure itself, but gives up at once on a handshake the
// server refused, however soon a renewed token would arrive (checked against the real
// client: connect_error with socket.active === false, and no further attempt). A refused
// token is retried a few times with the CURRENT token; calling socket.connect() again
// re-reads the `auth` function.
const MAX_AUTH_RETRIES = 3;
const AUTH_RETRY_DELAY_MS = 1500;

class SocketService {
  private connectionListeners: ((status: ConnectionStatus) => void)[] = [];
  private socket: Socket | null = null;
  private conversationId: string = '';
  private sessionId: string = '';
  private taskId: string = '';
  private teamspaceId: string = '';
  private teamspaceName: string = '';
  private loopId: string = '';
  private loopName: string = '';
  private isConnecting: boolean = false;
  // The token the server sees in the connect handshake. The server refuses a
  // connection without a valid token, and an access token lives for minutes while
  // this socket stays open for as long as the panel does, so the handshake must not
  // reuse the token the socket was first created with: every message the UI sends
  // carries its current token, and the most recent one is kept here for the next
  // (re)connect.
  private authToken: string = '';
  // Reads the live access token at handshake time. The auth library renews the
  // token silently while the panel sits idle, so the last token a message carried
  // can be long expired by the time the socket reconnects; this is preferred and
  // the last sent token is only the fallback.
  private liveToken: (() => string | undefined) | null = null;
  // Bounded retry after a refused handshake (see AUTH_REFUSALS).
  private authRetries = 0;
  private authRetryTimer: ReturnType<typeof setTimeout> | null = null;
  // The token presented on the latest handshake attempt, so a retry is only made
  // once a different one is available.
  private lastPresentedToken = '';
  // Guards against stacking duplicate event listeners: initializeSocketListeners()
  // in AIModule is called on every message send (not just on first connect), and
  // connect() below reuses `this.socket` whenever it's already connected rather
  // than creating a new one. Without this flag, every send added a full extra
  // set of `.on(...)` handlers to the same long-lived socket, so a single real
  // backend event (e.g. plan_approval_request) fired once per accumulated
  // listener -- surfacing as duplicate/phantom tool and plan cards that kept
  // "multiplying" with no new user action or network traffic involved. Reset
  // only when a genuinely new socket is created below, not on every call.
  private listenersBound: boolean = false;
  private notifyConnectionChange(isConnected: boolean, reason?: string) {
    this.connectionListeners.forEach((callback) =>
      callback({ isConnected, reason }),
    );
  }
  public onConnectionChange(
    callback: (status: ConnectionStatus) => void,
  ): void {
    this.connectionListeners.push(callback);
  }
  public removeConnectionListener(
    callback: (status: ConnectionStatus) => void,
  ): void {
    this.connectionListeners = this.connectionListeners.filter(
      (cb) => cb !== callback,
    );
  }
  /**
   * Connect to Socket.IO server
   * @param token - Authentication token
   * @param analyticsAIUrl - Base URL of analytics AI server
   * @param getLiveToken - Optional: returns the user's current access token, asked
   *   again on every connection attempt so an automatic reconnect never presents a
   *   token that was renewed since
   */
  connect(
    token: string,
    analyticsAIUrl: string,
    path: string,
    getLiveToken?: () => string | undefined,
  ): Promise<void> {
    // Taken on every call, including the early returns below: when the socket is
    // already connected (or connecting) a later caller's token and getter must
    // still replace the earlier ones. Otherwise a getter from an AiDataService
    // instance that no longer updates (AIModule unmounted and remounted without a
    // disconnect) would keep winning on the next reconnect with a frozen token.
    if (token) this.authToken = token;
    if (getLiveToken) this.liveToken = getLiveToken;
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // Wait for existing connection attempt
        const checkInterval = setInterval(() => {
          if (this.socket?.connected) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      this.isConnecting = true;
      this.listenersBound = false;
      this.clearAuthRetry();

      const url = new URL(analyticsAIUrl);
      const baseURL = `${url.protocol}//${url.host}`;
      this.socket = io(baseURL, {
        path: path,
        // A function, so Socket.IO asks again on every connection attempt
        // (including its automatic reconnects) instead of replaying a stale token.
        auth: (cb) => {
          this.lastPresentedToken = this.handshakeToken();
          cb({ token: this.lastPresentedToken });
        },
        transports: ['websocket', 'polling'],
        tryAllTransports: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      this.socket.on('connect', () => {
        this.isConnecting = false;
        this.authRetries = 0;
        this.notifyConnectionChange(true);
        resolve();
      });

      this.socket.on('connected', (data) => {});

      this.socket.on('disconnect', (reason) => {
        this.isConnecting = false;
        this.notifyConnectionChange(false, reason.toString());
      });

      const socket = this.socket;
      this.socket.on('connect_error', (error) => {
        console.error('[SocketService] Connection error:', error);
        this.isConnecting = false;
        this.notifyConnectionChange(false, error.message);
        reject(error);
        this.scheduleAuthRetry(socket, error);
      });

      // Timeout fallback
      setTimeout(() => {
        if (!this.socket?.connected) {
          this.isConnecting = false;
          reject(new Error('Connection timeout'));
        }
      }, 15000);
    });
  }

  /**
   * After the server refused the handshake for a token it could not verify, look for a
   * renewed token a bounded number of times and ask again with it.
   *
   * Only for that refusal: a transport failure is Socket.IO's own to retry
   * (socket.active), and any other error is not about the token. A retry is made
   * only once the current token differs from the one just refused: repeating a
   * refusal with the same token only repeats the refusal (the attempt still counts, so
   * the wait is bounded). It does not set isConnecting: connect() polls that flag with
   * no timeout, so a retry that never succeeds would hang a concurrent caller.
   */
  private scheduleAuthRetry(socket: Socket, error: Error): void {
    if (socket.active || !AUTH_REFUSALS.includes(error.message)) return;
    if (this.authRetries >= MAX_AUTH_RETRIES) return;
    this.authRetries += 1;
    if (this.authRetryTimer) clearTimeout(this.authRetryTimer);
    this.authRetryTimer = setTimeout(() => {
      this.authRetryTimer = null;
      // Replaced, disconnected, or already connected again: nothing to retry.
      if (this.socket !== socket || socket.connected) return;
      if (this.handshakeToken() === this.lastPresentedToken) {
        this.scheduleAuthRetry(socket, error);
        return;
      }
      socket.connect();
    }, AUTH_RETRY_DELAY_MS);
  }

  private clearAuthRetry(): void {
    if (this.authRetryTimer) clearTimeout(this.authRetryTimer);
    this.authRetryTimer = null;
    this.authRetries = 0;
  }

  /** The token for a connection attempt: the live one if it can be read, else the
   * last one the UI sent. A getter that throws or returns nothing is not an error. */
  private handshakeToken(): string {
    let live: string | undefined;
    try {
      live = this.liveToken?.();
    } catch {
      live = undefined;
    }
    if (live) this.authToken = live;
    return this.authToken;
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect(): void {
    // A logged-out user's token, and the closure over their auth context, must not
    // outlive the connection in this singleton.
    this.authToken = '';
    this.liveToken = null;
    this.clearAuthRetry();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      // Defensive, not confirmed-reachable (found in review of #989):
      // connect() only resets this on the branch that creates a genuinely
      // new socket, which relies on this.socket being null already. If
      // AIModule ever unmounts while the socket is still connected and
      // later remounts without disconnect() running first, connect() would
      // hit the already-connected branch, resolve immediately, and never
      // reach the reset — leaving the new instance's
      // initializeSocketListeners() seeing listenersAlreadyBound() === true
      // and skipping binding entirely, silently orphaning it from every
      // socket event. Whether that unmount/remount path is actually
      // reachable wasn't confirmed either way; resetting here costs
      // nothing and closes the gap regardless.
      this.listenersBound = false;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Whether AIModule has already bound its event listeners to the current
   * socket instance. False again only after a genuinely new socket is
   * created in connect() -- reusing an already-connected socket (the common
   * case across multiple sends in one turn-in-flight session) leaves this
   * true, so callers know not to bind a second time.
   */
  listenersAlreadyBound(): boolean {
    return this.listenersBound;
  }

  markListenersBound(): void {
    this.listenersBound = true;
  }

  /**
   * Send chat request for multistep workflow
   * @param data - Chat request data
   */
  sendChatRequest(data: {
    conversation_id: string;
    loop_name: string | null;
    loop_id?: string;
    message: string;
    teamspace_id: string;
    teamspace_name: string;
    specific_tool?: string;
    on_focus?: string[];
    token: string;
    task_id?: string;
    session_id?: string;
    tenant_id?: string;
  }): void {
    if (!this.socket?.connected) {
      throw new Error('Socket not connected. Call connect() first.');
    }

    if (data.token) this.authToken = data.token;
    this.conversationId = data.conversation_id;
    this.sessionId = data.session_id || '';
    this.taskId = data.task_id || '';
    this.teamspaceId = data.teamspace_id || '';
    this.teamspaceName = data.teamspace_name || '';
    this.loopId = data.loop_id || '';
    this.loopName = data.loop_name || '';

    this.socket.emit('chat_request', data);
  }

  /**
   * Resume a paused conversation with user's response.
   *
   * This is the primary method for continuing workflows that were paused
   * via LangGraph interrupt(). The resume_value becomes the return value
   * of the interrupt() call in the backend node.
   *
   * @param resumeValue - The user's response (e.g., clarification answers)
   * @param token - Authentication token
   */
  resumeConversation(resumeValue: any, token: string): void {
    if (!this.socket?.connected) {
      throw new Error('Socket not connected. Call connect() first.');
    }
    if (token) this.authToken = token;

    this.socket.emit('resume_conversation', {
      conversation_id: this.conversationId,
      session_id: this.sessionId,
      task_id: this.taskId,
      resume_value: resumeValue,
      token: token,
      teamspace_id: this.teamspaceId,
      teamspace_name: this.teamspaceName,
      loop_id: this.loopId,
      loop_name: this.loopName,
    });
  }

  /**
   * Send approval response
   * Uses resumeConversation under the hood for LangGraph interrupt support.
   * @param approved - Whether action is approved
   * @param reason - Optional reason for approval/rejection
   * @param token - Authentication token
   */
  sendApprovalResponse(
    approved: boolean,
    reason?: string,
    token?: string,
  ): void {
    if (!this.socket?.connected) {
      throw new Error('Socket not connected');
    }

    // Use new resume_conversation for LangGraph interrupts
    if (token) {
      this.resumeConversation({ approved, reason: reason || '' }, token);
    }
  }

  /**
   * Send clarification response
   * Uses resumeConversation under the hood for LangGraph interrupt support.
   * @param answer - Clarification answer (string or array of answers)
   * @param sessionId - Optional session ID to use instead of stored session_id
   * @param token - Authentication token
   * @param taskId - Optional task ID to ensure task-mode resumes stay in the same task conversation
   */
  sendClarificationResponse(
    answer: string | string[],
    sessionId?: string,
    token?: string,
    taskId?: string,
  ): void {
    if (!this.socket?.connected) {
      throw new Error('Socket not connected');
    }

    // Normalize answer to array format
    const answers = Array.isArray(answer) ? answer : [answer];

    // Use new resume_conversation for LangGraph interrupts
    if (token) {
      if (sessionId) {
        this.sessionId = sessionId;
      }
      // In task mode, preserve the taskId so resume_conversation uses
      // session_id::task_id as the effective conversation key.
      if (taskId) {
        this.taskId = taskId;
      }
      this.resumeConversation(answers, token);
    } else {
      // Legacy fallback
      this.socket.emit('clarification_response', {
        conversation_id: this.conversationId,
        session_id: sessionId || this.sessionId,
        answer: answers[0] || '',
      });
    }
  }

  /**
   * Listen for workflow events
   * @param callback - Callback function to handle events
   */
  onWorkflowEvent(callback: (event: StreamingEvent) => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('workflow_event', (event: StreamingEvent) => {
      callback(event);
    });
  }

  /**
   * Listen for approval requests
   * @param callback - Callback function to handle approval requests
   */
  onApprovalRequired(callback: (request: ApprovalRequest) => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('approval_required', (request: ApprovalRequest) => {
      callback(request);
    });
  }

  /**
   * Listen for clarification requests
   * @param callback - Callback function to handle clarification requests
   */
  onClarificationNeeded(
    callback: (request: ClarificationRequest) => void,
  ): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('clarifications_needed', (request: ClarificationRequest) => {
      callback(request);
    });
  }

  /**
   * Listen for chat completion
   * @param callback - Callback function to handle completion
   */
  onChatComplete(callback: (data: ChatCompleteEvent) => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('chat_complete', (data: ChatCompleteEvent) => {
      callback(data);
    });
  }

  /**
   * Listen for errors
   * @param callback - Callback function to handle errors
   */
  onError(
    callback: (error: { error: string; details?: string }) => void,
  ): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('chat_error', (error) => {
      console.error('[SocketService] Chat error:', error);
      callback(error);
    });
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners(): void {
    if (this.socket) {
      this.socket.removeAllListeners('workflow_event');
      this.socket.removeAllListeners('approval_required');
      this.socket.removeAllListeners('clarification_needed');
      this.socket.removeAllListeners('chat_complete');
      this.socket.removeAllListeners('chat_error');
      this.socket.removeAllListeners('tool_approval_request');
      this.socket.removeAllListeners('resume_turn_settled');
      this.socket.removeAllListeners('iteration_warning');
      this.socket.removeAllListeners('execution_terminal');
      this.socket.removeAllListeners('execution_receipt');
    }
  }

  /**
   * Get current conversation ID
   */
  getCurrentConversationId(): string {
    return this.conversationId;
  }

  /**
   * Listen for tool_approval_request — HITL middleware paused the run because
   * an MCP tool with requires_approval=true is about to be invoked.
   */
  onHitlApprovalRequest(
    callback: (request: ToolApprovalRequest) => void,
  ): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('tool_approval_request', (request: ToolApprovalRequest) => {
      callback(request);
    });
  }

  /**
   * Listen for resume_turn_settled — the backend's resume-turn call has
   * fully returned (clean finish, a fresh re-interrupt, or an error), on
   * every exit path. Unlike tool_approval_request (fired mid-run, once per
   * pending interrupt) or chat_complete (fired only on a clean finish), this
   * is the unambiguous "safe to send the next HITL decision" signal.
   */
  onResumeTurnSettled(callback: () => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('resume_turn_settled', () => {
      callback();
    });
  }

  onPlanApprovalRequest(
    callback: (request: PlanApprovalRequest) => void,
  ): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('plan_approval_request', (request: PlanApprovalRequest) => {
      callback(request);
    });
  }

  /**
   * Listen for iteration_warning — soft/hard/cap warnings from
   * IterationWarningMiddleware.
   */
  onIterationWarning(callback: (event: IterationWarningEvent) => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('iteration_warning', (event: IterationWarningEvent) => {
      callback(event);
    });
  }

  /**
   * Listen for execution_terminal — the end-of-run report for an approved plan
   * when no receipt covers it: some calls did not run (status needs_new_plan,
   * with refused_tools) or the run ended with no receipt (status unverified).
   * Sent live only; it is not stored with the turn.
   */
  onExecutionTerminal(callback: (event: ExecutionTerminalEvent) => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('execution_terminal', (event: ExecutionTerminalEvent) => {
      callback(event);
    });
  }

  /**
   * Listen for execution_receipt — the report produced after an approved plan
   * finished: what changed, what was skipped, what did not happen, and an
   * audit line per call. Also stored with the turn, so history restore shows
   * it too.
   */
  onExecutionReceipt(callback: (event: ExecutionReceiptEvent) => void): void {
    if (!this.socket) {
      console.warn('[SocketService] Socket not initialized');
      return;
    }
    this.socket.on('execution_receipt', (event: ExecutionReceiptEvent) => {
      callback(event);
    });
  }

  /**
   * Send an HITL approval/edit/reject/respond decision to resume the paused run.
   */
  sendHitlDecision(decision: HitlDecision, token: string): void {
    if (!this.socket?.connected) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('resume_conversation', {
      conversation_id: this.conversationId,
      session_id: this.sessionId,
      task_id: this.taskId,
      resume_value: decision,
      token,
      teamspace_id: this.teamspaceId,
      teamspace_name: this.teamspaceName,
      loop_id: this.loopId,
      loop_name: this.loopName,
    });
  }

  /**
   * Cancel the currently running conversation. Backend cancels the asyncio task
   * via CancelRegistry; expect chat_complete(success=false) in response.
   * Carries the live token (see below); a refused one comes back as chat_error
   * with error_code token_invalid.
   */
  abortTask(): void {
    if (!this.socket?.connected) {
      throw new Error('Socket not connected');
    }
    // The server needs a current token only once the one the socket connected with
    // has expired; send the live one (renewed silently while the panel is idle) so a
    // long-running turn can still be stopped after that.
    const token = this.handshakeToken();
    this.socket.emit('abort_task', {
      conversation_id: this.conversationId,
      session_id: this.sessionId,
      ...(token ? { token } : {}),
    });
  }
}

// Export singleton instance
export default new SocketService();
